/**
 * The purchase order's Now tab: the backend's rows in the one Now pattern
 * (`NowPanel`), the object as chips, each row's step as its one button and
 * the rest in its ⋯. A row's step records one delivery line, opening the
 * same dialog its row on the Receipts tab does, or (*Place with {supplier}*,
 * while it waits on To place) opens the placement page. (The order has its
 * action box instead: `OrderActionBox`.)
 */
import { Link } from "react-router";
import type {
  NowAction,
  NowItem,
  NowLink,
} from "@shared-core/data/order-to-invoice-types";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../primitives/button";
import { DropdownMenuItem } from "../../primitives/dropdown-menu";
import { NowPanel } from "../../patterns/NowPanel";
import { ObjectChip } from "../../patterns/ObjectChip";

/**
 * The page a chip opens, where the backend names: an order at its Order or
 * Delivery step (the order page has no tabs; its old Lines and Delivery tabs
 * are those steps), a purchase order's Lines or Receipts tab. A tab the
 * object does not have opens it as it first shows.
 */
function hrefOf(link: NowLink): string | undefined {
  switch (link.kind) {
    case "order":
      return link.order_no
        ? paths.orderDetail({
            orderNo: link.order_no,
            step:
              link.tab === "lines"
                ? "order"
                : link.tab === "delivery"
                  ? "delivery"
                  : undefined,
          })
        : undefined;
    case "purchase_order":
      return link.purchase_order_no
        ? paths.purchaseOrderDetail({
            purchaseOrderNo: link.purchase_order_no,
            tab: link.tab === "delivery" ? undefined : (link.tab ?? undefined),
          })
        : undefined;
  }
}

/** The delivery line a step records: every step the order pages offer records one. */
function recordedLineOf(step: NowAction | null): string | null {
  return step?.delivery_line_id ?? null;
}

export function OrderNowPanel({
  items,
  busy,
  onRecord,
  placementHref,
}: {
  items: NowItem[];
  busy: boolean;
  /** Open the recording dialog for a delivery line. */
  onRecord: (deliveryLineId: string) => void;
  /** The placement page, while the purchase order waits on To place: *Place with {supplier}* opens it. */
  placementHref?: string;
}) {
  return (
    <NowPanel
      items={items.map((item, i) => ({
        key: `${item.tone}-${i}`,
        tone: item.tone,
        lead: item.lead,
        objects:
          item.chips.length > 0 ? (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              {item.chips.map((chip) => (
                <ObjectChip
                  key={chip.label}
                  label={chip.label}
                  to={chip.link ? hrefOf(chip.link) : undefined}
                />
              ))}
            </span>
          ) : undefined,
        consequence: item.consequence,
        when: item.when,
        info: item.info,
        isLate: item.is_late,
        action:
          item.action?.kind === "place_with_supplier" && placementHref ? (
            <Button asChild size="sm" variant="default">
              <Link to={placementHref}>{item.action.label}</Link>
            </Button>
          ) : item.action && recordedLineOf(item.action) ? (
            <Button
              size="sm"
              variant={item.tone === "do" ? "default" : "outline"}
              disabled={busy}
              onClick={() => onRecord(recordedLineOf(item.action)!)}
            >
              {item.action.label}
            </Button>
          ) : undefined,
        more:
          item.more.length > 0
            ? item.more.map((step, j) => (
                <DropdownMenuItem
                  key={`${step.kind}-${step.delivery_line_id}-${j}`}
                  disabled={busy || !recordedLineOf(step)}
                  onSelect={() => onRecord(recordedLineOf(step)!)}
                >
                  {step.label}
                </DropdownMenuItem>
              ))
            : undefined,
      }))}
    />
  );
}
